/**
 * Connecting Google Analytics, browser side (the API half is
 * smagent-apiserver tinycms/services/analyticsConnect.js).
 *
 *   start            opens a popup at once (so the browser allows it), asks
 *                    the API for the Google sign-in address and sends the
 *                    popup there. The address's `state` is sealed by the API
 *                    and names this person and site.
 *   /auth/google-analytics/callback  (components/auth/GoogleAnalyticsCallback)
 *                    trades the code with the API for the person's GA4
 *                    properties and a ticket, posts them to the window that
 *                    opened it, and closes.
 *   useGoogleAnalyticsConnect  (components/cms/AnalyticsConnect)
 *                    connects the property that matches the site, or lets the
 *                    person pick.
 */

export const GA_PROPERTIES = 'GA_PROPERTIES';
export const GA_CONNECT_FAILED = 'GA_CONNECT_FAILED';

export interface GaStream {
  measurementId: string;
  uri: string | null;
}

export interface GaProperty {
  propertyId: string;
  name: string | null;
  accountName: string | null;
  streams: GaStream[];
}

/** What the callback page gets for a sign-in (no token, only a sealed ticket). */
export interface GaExchange {
  accountId: string;
  ticket: string | null;
  expiresAt: string;
  email: string | null;
  properties: GaProperty[];
  /** The property whose stream matches the site, when exactly one does. */
  suggested: string | null;
}

/** Where a finished sign-in also leaves its result, for a window that reloaded. */
export const PENDING_KEY = 'ga_connect:result';

export function stashPending(result: GaExchange) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(result));
  } catch {
    /* storage may be unavailable */
  }
}

/** Take (and clear) a result left by a popup whose opener was gone. */
export function takePending(): GaExchange | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_KEY);
    const r = JSON.parse(raw) as GaExchange;
    return r.expiresAt && Date.parse(r.expiresAt) > Date.now() ? r : null;
  } catch {
    return null;
  }
}

/** A GA4 deep link to the property's home, or GA itself. */
export const gaHomeUrl = (propertyId?: string | null) =>
  propertyId
    ? `https://analytics.google.com/analytics/web/#/p${propertyId}/reports/intelligenthome`
    : 'https://analytics.google.com/';
